import React, { FC, HTMLAttributes } from "react";
import { AppBar, Toolbar, Box, Drawer, List, ListItem, CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import { NAVIGATION_LIST_ITEM } from "../../data/navigation-list";
import { DRAWER_WIDTH_DESKTOP, DRAWER_WIDTH_MOBILE } from "../../data/constants";
import { useStyles } from "./style";
import clsx from "clsx";
import "./style.scss";
import history from "../../history";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { isMobileDevice } from "../../helpers/device-info-helper";
import { isSourcePos } from "../../helpers/common-helper";
import AccountControlComponent from "../../components/account-control";
import { CustomTooltip } from "../../components/reusable-ui/material-ui-comp/tooltip";
import { getFilteredTooltipOptions } from "../../helpers/common-helper";

interface NavigationDrawer extends HTMLAttributes<HTMLDivElement> {
  activeTabIndex?: number;
  window?: () => Window;
  source?: string;
  children?: any;
}

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 900,
      lg: 1200,
      xl: 1536
    }
  }
});

const drawerWidthDesktop = DRAWER_WIDTH_DESKTOP;
const drawerWidthMobile = DRAWER_WIDTH_MOBILE;

const NavigationDrawer: FC<NavigationDrawer> = ({ activeTabIndex, ...props }): JSX.Element => {
  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const container = window !== undefined ? () => window().document.body : undefined;
  const classes = useStyles();
  const { userData } = useSelector((state: RootState) => ({
    userData: state.businessReducer.userData
  }));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <div>
      <div className="dotpe-icon h-75 nav-item px-20 py-10 flex items-center justify-center sm:justify-start">
        <div className="rista-sprite ic-arrow-left hidden sm:block mr-10" onClick={handleDrawerToggle}></div>
        <div className="rista-sprite ic-dotpe"></div>
      </div>
      <List>
        {NAVIGATION_LIST_ITEM().map((item, index) => {
          let isShow = true;
          if ((item.permission && item.permission?.length && !item.permission.includes(userData.role))
            || (isMobileDevice() && item.name === `Catalogue`)
          ) {
            isShow = false;
          }
          let subTabList = item?.subTabsMap ? Object.values(getFilteredTooltipOptions(item?.subTabsMap)): [];
          return (
            (isShow && (!item?.subTabsMap || subTabList?.length))
            ? (subTabList?.length > 1
              ? <CustomTooltip
                  title={renderTooltipTitle(subTabList, item.path)}
                  // enterTouchDelay={0}
                  leaveTouchDelay={3000}
                  // disableFocusListener
                  arrow
                  placement={isMobileDevice() ? "bottom": "right"}
                  sx={{minWidth: 200}}
                  key={index}
                >
                  {renderTabListItem(item, index)}
                </CustomTooltip>
              : renderTabListItem(item, index)
            ): null
          )
        })}
      </List>
    </div>
  );

  function renderTooltipTitle(subTabList: Array<any>, path: string) {
    return <>
      {
        subTabList.map((tabItem: any, index) => {
          return (
            <p
              className={`text-15 text-[#666666] font-semibold cursor-pointer hover:text-[#0074FB]
                ${index > 0? "border-t border-[#EBEBEB] pt-8 mt-8": ""}
              `}
              key={index}
              onClick={() => history.push(path + "/" + tabItem.tabId)}
            >
              {tabItem?.text}
            </p>
          )
        })
      }
    </>
  }

  function renderTabListItem(item: any, index: number) {
    return (
      <ListItem key={index} disablePadding
        className={`nav-item relative h-75 px-20 py-10 sm:h-[50px] cursor-pointer flex items-center justify-center hover:bg-greyF4F4F4/70 sm:justify-start
          ${item.tabIndex === activeTabIndex? 'bg-[#E8E8E8]': ''}
        `}
        onClick={() => history.push(item.path)}>
        {
          item.tabIndex === activeTabIndex ?
            <div className="absolute top-0 bottom-0 left-0 bg-blue0074FB w-6"></div>
            : null
        }
        <div className={`flex flex-col sm:flex-row items-center justify-center`}>
          <div className={item.icon}></div>
          <div className={clsx(classes.navText, "text-12 sm:text-18 sm:font-semibold sm:ml-10 font-regular whitespace-nowrap greya5a5a5 select-none")}>{item.name}</div>
        </div>
      </ListItem>
    )
  }

  return (
    isSourcePos() ? <></> :
    <ThemeProvider
      theme={theme}>
      <Box
        className="side-nav-main-container"
        component="nav"
        sx={{ width: { sm: drawerWidthDesktop }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <AppBar
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${drawerWidthDesktop}px)` },
            ml: { sm: `${drawerWidthDesktop}px` },
          }}
        >
          <Toolbar className="hidden sm:flex sm:justify-between bg-white shadow-[0px_2px_4px_#0000001A]">
            <div className="flex items-center">
              <div
                className="rista-sprite ic-menu cursor-pointer mr-[10px]"
                onClick={handleDrawerToggle}
              >
              </div>
              <div className="rista-sprite ic-dotpe"></div>
            </div>
            {isMobileDevice()? <AccountControlComponent />: null}
          </Toolbar>
        </AppBar>
        <CssBaseline></CssBaseline>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          className={"sm:block hidden"}
          sx={{
            // display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidthMobile },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          className={"sm:hidden block"}
          sx={{
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidthDesktop },
          }}
          open
        >
          {drawer}
        </Drawer>

        {/* <main className={clsx(classes.content, "relative bg-greyFAFAFA pt-101 px-20 pb-20")}>{props.children}</main> */}
      </Box>
    </ThemeProvider>
  );
};

export default NavigationDrawer;